import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { motion } from 'framer-motion';
import { useAccountStore } from '../../stores/account';
import { ScreenPlate } from '../../components/ScreenPlate';
import { useMotionTier } from '../../components/useMotionTier';
import { useTransitionStore, measureRect } from '../../stores/transition';
import { duration, ease } from '../../theme/motion';
import { sfx } from '../../audio/sfx';

// Hero-only welcome: title sequences in, single CTA. Faction details live in
// the OnboardingModal, which now fires after the tape transition rather than
// on top of it.
//
// Motion sequence — re-cut from the original 1.6s serial fade. The old version
// put the Play button on screen at 1.30s, which on a first run landed 4.15s
// after boot once the splash hold was counted. Same beats, denser, and now
// interruptible: any tap or key during the entrance snaps everything to its
// final state.
//
//   0.00  Cholos slides in from the left
//   0.18  vs. stamps in (scale punch + rotate)
//   0.26  Emos slides in from the right
//   0.52  subtitle
//   0.64  CTA
//   0.78  footer
//
// The Play CTA does NOT navigate directly. It hands off to the tape transport
// (components/transition/TransitionHost.tsx), which owns the navigation and
// fires it behind a full-screen cover.

const ENTRANCE_TOTAL_MS = 1300;

export function WelcomePage() {
  const setHasPlayed = useAccountStore((s) => s.setHasPlayed);
  const tier = useMotionTier();
  const navigate = useNavigate();
  const startTransition = useTransitionStore((s) => s.start);

  const titleRef = useRef<HTMLHeadingElement | null>(null);
  const [instant, setInstant] = useState(false);
  const reduced = tier === 'reduced';

  // Skip-on-input. The original had no way to short-circuit the entrance —
  // a returning player sat through the full sequence every time. Listener is
  // torn down once the entrance is over so it can't leak into the CTA press.
  useEffect(() => {
    if (reduced) return;
    function skip() {
      setInstant(true);
    }
    window.addEventListener('pointerdown', skip);
    window.addEventListener('keydown', skip);
    const t = window.setTimeout(() => {
      window.removeEventListener('pointerdown', skip);
      window.removeEventListener('keydown', skip);
    }, ENTRANCE_TOTAL_MS);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener('pointerdown', skip);
      window.removeEventListener('keydown', skip);
    };
  }, [reduced]);

  /** Timing helper — collapses to zero when skipped or on reduced motion. */
  const beat = (delay: number, dur: number) =>
    instant || reduced
      ? { duration: 0, delay: 0 }
      : { delay, duration: dur, ease: ease.out };

  const fromLeft = reduced ? { opacity: 1, x: 0 } : { opacity: 0, x: -56 };
  const fromRight = reduced ? { opacity: 1, x: 0 } : { opacity: 0, x: 56 };
  const stampInitial = reduced
    ? { opacity: 1, scale: 1, rotate: 0 }
    : { opacity: 0, scale: 0.5, rotate: -8 };

  function handlePlay(e: React.MouseEvent) {
    // Hand the navigation to the transport. If it declines (a run is already
    // in flight from a double-tap), let the Link's own navigation stand so the
    // player can never get stuck on this screen.
    const title = measureRect(titleRef.current);
    const started = startTransition({
      kind: 'tape',
      commit: () => navigate('/menu'),
      slots: title ? { title } : {},
    });
    if (started) {
      e.preventDefault();
      sfx.click();
      setHasPlayed(true);
    }
  }

  return (
    <main className="pt-safe relative h-dvh w-full overflow-hidden bg-asphalt">
      {/* The shared wall itself. Light scrim (0.55) — this is the one screen that
          is allowed to be mostly picture, since the only UI over it is a title,
          one line of copy and a single CTA. */}
      <ScreenPlate src="plates/parking_lot.webp" scrim={0.55} />

      {/* Ambient sodium vignettes — two washes from opposite corners give the
          parking-lot lamp feel. Static on reduced motion. */}
      <motion.div
        aria-hidden
        className="pointer-events-none absolute -left-1/4 -top-1/4 h-[60vw] w-[60vw] max-h-[900px] max-w-[900px]"
        style={{
          background:
            'radial-gradient(circle, rgba(255,138,61,0.22) 0%, rgba(255,138,61,0) 70%)',
        }}
        animate={reduced ? undefined : { x: [0, 12, 0], y: [0, 8, 0] }}
        transition={
          reduced ? undefined : { duration: 14, ease: ease.inOut, repeat: Infinity }
        }
      />
      <motion.div
        aria-hidden
        className="pointer-events-none absolute -bottom-1/4 -right-1/4 h-[50vw] w-[50vw] max-h-[760px] max-w-[760px]"
        style={{
          background:
            'radial-gradient(circle, rgba(255,46,126,0.18) 0%, rgba(255,46,126,0) 70%)',
        }}
        animate={reduced ? undefined : { x: [0, -10, 0], y: [0, -6, 0] }}
        transition={
          reduced ? undefined : { duration: 18, ease: ease.inOut, repeat: Infinity }
        }
      />

      {/* Key art slot — intentionally empty. The face-off group that sat here was
          drawn in the old animated-cartoon house style, which the art direction
          replaced with two per-faction hand-media languages (see the CHOLO_HOUSE /
          ROCKER_HOUSE blocks in tools/portrait_gen_lib.py). A cholo and a rocker
          now have to be drawn in DIFFERENT media, so a single shared-style pair
          image is no longer the right asset — this needs re-thinking as two
          separate figures, not a redraw. */}

      <section className="relative mx-auto flex h-full max-w-screen-2xl flex-col items-center justify-center gap-xs px-md py-xs [@media(max-height:500px)]:gap-[2px] md:gap-lg md:px-xl md:py-md lg:gap-xl">
        {/* The ref is the transition's source geometry: the tape transport
            measures this element to know where the travelling wordmark starts. */}
        <h1
          ref={titleRef}
          className="flex flex-col items-center gap-2xs leading-none md:flex-row md:items-baseline md:gap-md"
        >
          <motion.span
            initial={fromLeft}
            animate={{ opacity: 1, x: 0 }}
            transition={beat(0, duration.settle)}
            className="font-cholo text-[26px] text-cholo [@media(max-height:500px)]:text-[28px] sm:text-[36px] md:text-[60px] lg:text-[104px] xl:text-[136px]"
          >
            Cholos
          </motion.span>
          <motion.span
            initial={stampInitial}
            animate={
              reduced || instant
                ? { opacity: 1, scale: 1, rotate: 0 }
                : { opacity: 1, scale: [0.5, 1.15, 1], rotate: [-8, 2, -3] }
            }
            transition={
              instant || reduced
                ? { duration: 0 }
                : { delay: 0.18, duration: duration.settle, ease: ease.punch }
            }
            className="font-data text-[13px] tracking-widest text-sodium [@media(max-height:500px)]:text-[14px] sm:text-[18px] md:text-[28px] lg:text-[42px] xl:text-[54px]"
          >
            {/* Subtle infinite pulse after the entrance settles. */}
            <motion.span
              animate={reduced ? undefined : { opacity: [0.85, 1, 0.85], scale: [1, 1.04, 1] }}
              transition={
                reduced
                  ? undefined
                  : { delay: 1.1, duration: 2.2, ease: ease.inOut, repeat: Infinity }
              }
              className="inline-block"
            >
              vs.
            </motion.span>
          </motion.span>
          <motion.span
            initial={fromRight}
            animate={{ opacity: 1, x: 0 }}
            transition={beat(0.26, duration.settle)}
            className="font-emo text-[26px] text-emo [@media(max-height:500px)]:text-[28px] sm:text-[36px] md:text-[60px] lg:text-[104px] xl:text-[136px]"
          >
            Emos
          </motion.span>
        </h1>

        <motion.p
          initial={reduced ? { opacity: 1 } : { opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={beat(0.52, duration.enter)}
          className="max-w-xs text-center text-[13px] text-marker [@media(max-height:500px)]:text-[10px] md:max-w-xl md:text-[16px] lg:max-w-2xl lg:text-[20px]"
        >
          Parking lot at 1am, two crews share the same wall.
        </motion.p>

        <motion.div
          initial={reduced ? { opacity: 1, y: 0 } : { opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={beat(0.64, duration.enter)}
          whileHover={reduced ? undefined : { scale: 1.04 }}
          whileTap={{ scale: 0.97 }}
        >
          <Link
            to="/menu"
            onClick={handlePlay}
            className="inline-block min-h-[44px] rounded-sm bg-sodium px-lg py-sm font-data text-[13px] uppercase tracking-[0.2em] text-sharpie shadow-sodium transition-colors hover:bg-sodiumGlow sm:text-[14px] md:min-h-[52px] md:px-2xl md:py-md md:text-[18px] lg:py-lg lg:text-[22px] lg:tracking-[0.25em]"
          >
            Play
          </Link>
        </motion.div>

        <motion.p
          initial={reduced ? { opacity: 1 } : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={beat(0.78, duration.enter)}
          className="font-data text-[12px] uppercase tracking-[0.3em] text-graphite md:text-[12px] lg:text-[14px]"
        >
          v2 · {new Date().getFullYear()} · serifeus studio
        </motion.p>
      </section>
    </main>
  );
}
